import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const token = searchParams.get("token");
  const calledRef = useRef(false);

  useEffect(() => {
    if (!token) { setStatus("error"); return; }
    if (calledRef.current) return;
    calledRef.current = true;

    const verify = async () => {
      try {
        const res = await api.get(`/auth/verify-email?token=${token}`);
        setStatus(res.data.message?.includes("already") ? "already" : "success");
      } catch {
        // Token may have been consumed by a parallel/duplicate request.
        // Check if the user is actually verified by trying to get their profile.
        try {
          const me = await api.get("/auth/me");
          if (me.data?.email_verified) {
            setStatus("success");
            return;
          }
        } catch {
          // Not logged in — can't check, show error
        }
        setStatus("error");
      }
    };
    verify();
  }, [token]);

  return (
    <div className="min-h-[60vh] bg-[#FAFAF8] flex items-center justify-center px-4" data-testid="verify-email-page">
      <div className="bg-white border border-stone-200 rounded-2xl p-10 max-w-md w-full text-center">
        {status === "loading" && (
          <>
            <Loader2 className="w-10 h-10 text-coral mx-auto mb-4 animate-spin" />
            <p className="text-stone-600 font-ui">Verifying your email...</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-7 h-7" />
            </div>
            <h1 className="text-xl text-stone-900 font-medium mb-2" style={{ fontFamily: "'Fraunces', serif" }}>Email verified</h1>
            <p className="text-stone-500 text-sm font-ui mb-6">Your email has been verified. You can now create listings, book items, and message other users.</p>
            <Link to="/dashboard"><Button className="rounded-full px-8 bg-coral hover:bg-[#d44814] text-white font-ui">Go to Dashboard</Button></Link>
          </>
        )}
        {status === "already" && (
          <>
            <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-4" />
            <h1 className="text-xl text-stone-900 font-medium mb-2" style={{ fontFamily: "'Fraunces', serif" }}>Already verified</h1>
            <p className="text-stone-500 text-sm font-ui mb-6">Your email address has already been verified.</p>
            <Link to="/dashboard"><Button className="rounded-full px-8 bg-coral hover:bg-[#d44814] text-white font-ui">Go to Dashboard</Button></Link>
          </>
        )}
        {status === "error" && (
          <>
            <div className="w-14 h-14 rounded-full bg-red-50 text-red-500 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-7 h-7" />
            </div>
            <h1 className="text-xl text-stone-900 font-medium mb-2" style={{ fontFamily: "'Fraunces', serif" }}>Verification failed</h1>
            <p className="text-stone-500 text-sm font-ui mb-6">This link is invalid or has expired. Please request a new verification email.</p>
            <Link to="/dashboard"><Button className="rounded-full px-8 bg-coral hover:bg-[#d44814] text-white font-ui">Go to Dashboard</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
